/**
 * The match-setup form (PIVOT-299) — `GET`/`PUT /api/onboarding/match-setup`.
 *
 * The matcher-v2 sibling of `ConstraintsStep` (PIVOT-232): the same kind of
 * deterministic, zero-model-call form between the interview and finalize, but
 * writing typed policy rules instead of profile fields, and asking two things
 * the legacy form never did — working hours and excluded work — while
 * dropping the commute-anchor question (out of scope for this ticket; see
 * `pivot.onboarding.match_setup`'s module docstring on the backend).
 *
 * Every hard question is a value-or-marker pair, exactly like
 * `ConstraintsStep`: "we never asked" and "anywhere is fine" must stay
 * distinguishable. Shares that component's layout via `styles/step-form.css`
 * rather than duplicating it (frontend/CLAUDE.md §3's ownership rule — moved
 * there the moment a second component needed the same shape).
 *
 * Owns its own read the way `ConstraintsStep` and `CvUpload` do (CLAUDE.md
 * §2): fetches its seed, so a reload mid-step costs nothing.
 */

import { useEffect, useState } from 'react'
import { getMatchSetup, saveMatchSetup } from '../api/onboarding'
import { useApiResource } from '../hooks/useApiResource'
import { useI18n } from '../i18n'
import type { TranslationKey } from '../i18n/types'
import {
  CONTRACT_TYPES,
  LEVELS,
  PAY_GROSS_OR_NET,
  PAY_PERIODS,
  PROVINCES,
  SCHEDULE_KINDS,
  splitLocations,
} from '../lib/constraints'
import type {
  EmployerExclusionAnswer,
  EmployerExclusionScope,
  Locale,
  MatchSetupAnswers,
  RuleStrength,
} from '../types/domain'
import { Icon } from './Icon'
import '../styles/step-form.css'
import '../styles/location-chips.css'

interface MatchSetupFormProps {
  /** Called once the answers are stored server-side; the page finalizes next. */
  onSaved: () => void
}

const LANGUAGES: { value: Locale; labelKey: TranslationKey }[] = [
  { value: 'fr', labelKey: 'onboarding.constraintsLanguageFrench' },
  { value: 'nl', labelKey: 'onboarding.constraintsLanguageDutch' },
  { value: 'en', labelKey: 'onboarding.constraintsLanguageEnglish' },
]

const CURRENCIES = ['EUR', 'USD', 'GBP']

/**
 * The employer refusals this form can state (PIVOT-370). TWO rows, never one
 * with a "large" toggle: the contract keeps "any commercial company" and
 * "large corporate groups" as separate rules that never imply each other, and
 * one control with a modifier is the shape that invites deriving the second
 * from the first. The backend's third scope (a named list of organization
 * kinds) has no control here yet -- it is reachable through the policy draft,
 * not this form.
 */
const EMPLOYER_SCOPES: { value: EmployerExclusionScope; labelKey: TranslationKey }[] = [
  { value: 'all_commercial', labelKey: 'onboarding.matchSetupEmployerAllCommercial' },
  { value: 'large_corporate', labelKey: 'onboarding.matchSetupEmployerLargeCorporate' },
]

/** Hard removes the job; soft only ranks it down. Both spelled out, because
 * the person picks one and there is no default either way. */
const EMPLOYER_STRENGTHS: { value: RuleStrength; labelKey: TranslationKey }[] = [
  { value: 'hard', labelKey: 'onboarding.matchSetupEmployerStrengthHard' },
  { value: 'soft', labelKey: 'onboarding.matchSetupEmployerStrengthSoft' },
]

/** What the form holds while it is being filled in, before it becomes a body. */
interface FormState {
  /** Ticked province codes -- the new picker (PIVOT-352). */
  locationCodes: string[]
  /** Free-text strings from a policy answered before this ticket. Shown as
   * removable chips only: there is no control that adds to this list. */
  legacyLocations: string[]
  locationsNoPreference: boolean
  contracts: string[]
  contractsNoPreference: boolean
  scheduleAllowed: string[]
  scheduleExcluded: string[]
  scheduleNoPreference: boolean
  payAmount: string
  payCurrency: string
  payPeriod: string
  payGrossOrNet: string
  payNoPreference: boolean
  exclusions: string
  /** Ticked employer scopes and the strength chosen for each. A scope
   * absent from this map is a refusal the person did not make. */
  employerStrengths: Partial<Record<EmployerExclusionScope, RuleStrength>>
  levels: Record<Locale, string>
}

const EMPTY: FormState = {
  locationCodes: [],
  legacyLocations: [],
  locationsNoPreference: false,
  contracts: [],
  contractsNoPreference: false,
  scheduleAllowed: [],
  scheduleExcluded: [],
  scheduleNoPreference: false,
  payAmount: '',
  payCurrency: CURRENCIES[0],
  payPeriod: 'month',
  payGrossOrNet: 'gross',
  payNoPreference: false,
  exclusions: '',
  employerStrengths: {},
  // '' is the disabled placeholder LEVELS opens on (PIVOT-350) -- "never
  // touched this select", read by `toAnswers` the same way an omitted
  // language always has been: no fact stated, not a low one.
  levels: { fr: '', nl: '', en: '' },
}

function splitLines(value: string): string[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
}

/** Re-open the form on what was saved, so a reload costs no retyping. */
function seedFrom(answers: MatchSetupAnswers): FormState {
  const levels: Record<Locale, string> = { fr: '', nl: '', en: '' }
  for (const entry of answers.language_answers) {
    // A row saved before PIVOT-350 can hold the literal `unstated` level --
    // no longer an option in `LEVELS`, so it reopens on the placeholder
    // (the candidate must re-answer) rather than a `<select>` value with no
    // matching `<option>`.
    if (entry.language in levels) {
      levels[entry.language] = entry.level === 'unstated' ? '' : entry.level
    }
  }
  // A4: a policy answered before this ticket carries only free-text town
  // names here; one answered on the new grid carries only province codes; a
  // policy mid-migration can carry both, and both render.
  const { provinceCodes, legacy } = splitLocations(answers.locations)
  return {
    locationCodes: provinceCodes,
    legacyLocations: legacy,
    locationsNoPreference: answers.locations_no_preference,
    contracts: answers.contract_types,
    contractsNoPreference: answers.contract_types_no_preference,
    scheduleAllowed: answers.schedule_allowed,
    scheduleExcluded: answers.schedule_excluded,
    scheduleNoPreference: answers.schedule_no_preference,
    payAmount: answers.pay_amount === null ? '' : String(answers.pay_amount),
    payCurrency: answers.pay_currency ?? CURRENCIES[0],
    payPeriod: answers.pay_period ?? 'month',
    payGrossOrNet: answers.pay_gross_or_net ?? 'gross',
    payNoPreference: answers.pay_no_preference,
    exclusions: answers.exclusions.join('\n'),
    // A4: reopening the form shows the refusal that was actually made,
    // strength included, never a re-defaulted one.
    employerStrengths: Object.fromEntries(
      answers.employer_exclusions.map((entry) => [entry.scope, entry.strength]),
    ),
    levels,
  }
}

function toAnswers(form: FormState): MatchSetupAnswers {
  const amount = Number.parseInt(form.payAmount, 10)
  return {
    locations: form.locationsNoPreference
      ? []
      : [...form.locationCodes, ...form.legacyLocations],
    locations_no_preference: form.locationsNoPreference,
    contract_types: form.contractsNoPreference
      ? []
      : (form.contracts as MatchSetupAnswers['contract_types']),
    contract_types_no_preference: form.contractsNoPreference,
    schedule_allowed: form.scheduleNoPreference
      ? []
      : (form.scheduleAllowed as MatchSetupAnswers['schedule_allowed']),
    schedule_excluded: form.scheduleNoPreference
      ? []
      : (form.scheduleExcluded as MatchSetupAnswers['schedule_excluded']),
    schedule_no_preference: form.scheduleNoPreference,
    pay_amount: form.payNoPreference || Number.isNaN(amount) ? null : amount,
    pay_currency: form.payNoPreference ? null : form.payCurrency,
    pay_period: form.payNoPreference ? null : (form.payPeriod as MatchSetupAnswers['pay_period']),
    pay_gross_or_net: form.payNoPreference
      ? null
      : (form.payGrossOrNet as MatchSetupAnswers['pay_gross_or_net']),
    pay_no_preference: form.payNoPreference,
    exclusions: splitLines(form.exclusions),
    employer_exclusions: EMPLOYER_SCOPES.flatMap(({ value }): EmployerExclusionAnswer[] => {
      const strength = form.employerStrengths[value]
      // No strength means the box is unticked, so the person stated no
      // refusal for this scope and no rule is sent for it.
      return strength ? [{ scope: value, organization_types: [], strength }] : []
    }),
    language_answers: LANGUAGES.filter(({ value }) => form.levels[value] !== '').map(
      ({ value }) => ({
        language: value,
        level: form.levels[value] as MatchSetupAnswers['language_answers'][number]['level'],
      }),
    ),
  }
}

/**
 * Whether every hard question carries a value or its no-preference marker —
 * the same rule the server enforces, checked here so the refusal is a
 * sentence rather than a 422.
 *
 * PIVOT-350 deliberately does NOT force a language choice here, unlike
 * `ConstraintsStep`. The two forms answer to two different contracts: the v1
 * form pairs its forced choice with a `languagesNone` opt-out, so a candidate
 * can still decline. This form has no such box, and the matcher-v2 plan
 * (§1.4) requires the escape it would otherwise remove — "an unstated level
 * stays unknown and does not create needs_check by itself", and "an unasked
 * field never stores a value the candidate did not give". Forcing three
 * answers with no way out would store exactly such a value. What this ticket
 * adds here is the missing WORD ("I don't speak it") and the gate that
 * honours it, never a compulsion to use it.
 */
function isComplete(answers: MatchSetupAnswers): boolean {
  return (
    answers.locations.length > 0 !== answers.locations_no_preference &&
    answers.contract_types.length > 0 !== answers.contract_types_no_preference &&
    (answers.schedule_allowed.length > 0 || answers.schedule_excluded.length > 0) !==
      answers.schedule_no_preference &&
    (answers.pay_amount !== null) !== answers.pay_no_preference
  )
}

export function MatchSetupForm({ onSaved }: MatchSetupFormProps) {
  const { t } = useI18n()
  const saved = useApiResource(getMatchSetup)
  const [form, setForm] = useState<FormState | null>(null)
  const [error, setError] = useState<TranslationKey | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    // One-way, like `ConstraintsStep`'s own seed effect: the seed decides what
    // the form OPENS on, and after that the candidate's typing owns it.
    if (form !== null || saved.loading) return
    setForm(saved.data?.answers ? seedFrom(saved.data.answers) : EMPTY)
  }, [saved.loading, saved.data, form])

  if (form === null) {
    // An ordinary indexed read, so the plain loading line rather than
    // `Thinking`, which is reserved for waits on a model (CLAUDE.md §9).
    return <output>{t('common.loading')}</output>
  }

  const patch = (change: Partial<FormState>) => setForm({ ...form, ...change })

  const toggleInList = (list: string[], value: string): string[] =>
    list.includes(value) ? list.filter((item) => item !== value) : [...list, value]

  const submit = async () => {
    const answers = toAnswers(form)
    if (!isComplete(answers)) {
      setError('onboarding.matchSetupIncomplete')
      return
    }
    setError(null)
    setSaving(true)
    try {
      await saveMatchSetup(answers)
    } catch {
      // Stay on the step with an honest error. Advancing would finalize a
      // profile whose search setup was never recorded.
      setError('onboarding.matchSetupSaveFailed')
      setSaving(false)
      return
    }
    setSaving(false)
    onSaved()
  }

  return (
    <div className="stack">
      <h2>{t('onboarding.matchSetupTitle')}</h2>
      <p>{t('onboarding.matchSetupBody')}</p>

      <div className="step-form">
        <fieldset className="step-form-field">
          <legend className="step-form-label">{t('onboarding.matchSetupLocationsLabel')}</legend>
          <div className="step-form-choices">
            {PROVINCES.map((province) => (
              <label key={province.value} className="step-form-choice">
                <input
                  type="checkbox"
                  checked={form.locationCodes.includes(province.value)}
                  disabled={form.locationsNoPreference}
                  onChange={() =>
                    patch({ locationCodes: toggleInList(form.locationCodes, province.value) })
                  }
                />
                {t(province.labelKey)}
              </label>
            ))}
          </div>
          {/* A4: a legacy free-text value from a policy answered before this
              ticket -- removable only, since there is no control left that
              writes a new one of these. */}
          {form.legacyLocations.length > 0 && (
            <ul className="location-chip-list">
              {form.legacyLocations.map((location) => (
                <li key={location} className="location-chip">
                  {location}
                  <button
                    type="button"
                    className="location-chip-remove"
                    disabled={form.locationsNoPreference}
                    aria-label={t('onboarding.locationChipRemove', { location })}
                    onClick={() =>
                      patch({
                        legacyLocations: form.legacyLocations.filter((v) => v !== location),
                      })
                    }
                  >
                    <Icon name="cross" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="step-form-hint">{t('onboarding.matchSetupLocationsHint')}</p>
          <label className="step-form-opt-out">
            <input
              type="checkbox"
              checked={form.locationsNoPreference}
              onChange={(event) => patch({ locationsNoPreference: event.target.checked })}
            />
            {t('onboarding.matchSetupLocationsNoPreference')}
          </label>
        </fieldset>

        <fieldset className="step-form-field">
          <legend className="step-form-label">{t('onboarding.matchSetupContractLabel')}</legend>
          <div className="step-form-choices">
            {CONTRACT_TYPES.map((contract) => (
              <label key={contract.value} className="step-form-choice">
                <input
                  type="checkbox"
                  checked={form.contracts.includes(contract.value)}
                  disabled={form.contractsNoPreference}
                  onChange={() => patch({ contracts: toggleInList(form.contracts, contract.value) })}
                />
                {t(contract.labelKey)}
              </label>
            ))}
          </div>
          <label className="step-form-opt-out">
            <input
              type="checkbox"
              checked={form.contractsNoPreference}
              onChange={(event) => patch({ contractsNoPreference: event.target.checked })}
            />
            {t('onboarding.matchSetupContractNoPreference')}
          </label>
        </fieldset>

        <fieldset className="step-form-field">
          <legend className="step-form-label">{t('onboarding.matchSetupScheduleLabel')}</legend>
          <p className="step-form-hint">{t('onboarding.matchSetupScheduleHint')}</p>
          <p className="step-form-hint">{t('onboarding.matchSetupScheduleAllowedLabel')}</p>
          <div className="step-form-choices">
            {SCHEDULE_KINDS.map((kind) => (
              <label key={`allowed-${kind.value}`} className="step-form-choice">
                <input
                  type="checkbox"
                  checked={form.scheduleAllowed.includes(kind.value)}
                  disabled={form.scheduleNoPreference}
                  // The visible text ("Daytime") repeats in the excluded group
                  // below; an explicit `aria-label` keeps the two checkboxes'
                  // accessible names distinct ("Fine with: Daytime" vs. "Not
                  // for me: Daytime") without changing what is shown on screen.
                  aria-label={`${t('onboarding.matchSetupScheduleAllowedLabel')}: ${t(kind.labelKey)}`}
                  onChange={() =>
                    patch({
                      scheduleAllowed: toggleInList(form.scheduleAllowed, kind.value),
                      // The backend rejects a schedule kind in both lists at
                      // once (`ScheduleValue.validate_schedule`), and this is
                      // the only pair of controls that can produce that: two
                      // independent checkbox groups for the same vocabulary,
                      // nothing else stopping the same kind being ticked in
                      // both. Picking it here has to drop it from there
                      // (PIVOT-299 review) rather than let the whole form
                      // fail to save over one contradiction.
                      scheduleExcluded: form.scheduleExcluded.filter((v) => v !== kind.value),
                    })
                  }
                />
                {t(kind.labelKey)}
              </label>
            ))}
          </div>
          <p className="step-form-hint">{t('onboarding.matchSetupScheduleExcludedLabel')}</p>
          <div className="step-form-choices">
            {SCHEDULE_KINDS.map((kind) => (
              <label key={`excluded-${kind.value}`} className="step-form-choice">
                <input
                  type="checkbox"
                  checked={form.scheduleExcluded.includes(kind.value)}
                  disabled={form.scheduleNoPreference}
                  aria-label={`${t('onboarding.matchSetupScheduleExcludedLabel')}: ${t(kind.labelKey)}`}
                  onChange={() =>
                    patch({
                      scheduleExcluded: toggleInList(form.scheduleExcluded, kind.value),
                      // Symmetric with the allowed group's own handler above.
                      scheduleAllowed: form.scheduleAllowed.filter((v) => v !== kind.value),
                    })
                  }
                />
                {t(kind.labelKey)}
              </label>
            ))}
          </div>
          <label className="step-form-opt-out">
            <input
              type="checkbox"
              checked={form.scheduleNoPreference}
              onChange={(event) => patch({ scheduleNoPreference: event.target.checked })}
            />
            {t('onboarding.matchSetupScheduleNoPreference')}
          </label>
        </fieldset>

        <div className="step-form-field">
          <label htmlFor="match-setup-pay-amount" className="step-form-label">
            {t('onboarding.matchSetupPayLabel')}
          </label>
          <p className="step-form-hint">{t('onboarding.matchSetupPayHint')}</p>
          <div className="step-form-choices">
            <label className="step-form-choice">
              {t('onboarding.matchSetupPayAmountLabel')}
              <input
                id="match-setup-pay-amount"
                type="number"
                min={0}
                step={50}
                value={form.payAmount}
                disabled={form.payNoPreference}
                onChange={(event) => patch({ payAmount: event.target.value })}
              />
            </label>
            <label className="step-form-choice">
              {t('onboarding.matchSetupPayCurrencyLabel')}
              <select
                value={form.payCurrency}
                disabled={form.payNoPreference}
                onChange={(event) => patch({ payCurrency: event.target.value })}
              >
                {CURRENCIES.map((currency) => (
                  <option key={currency} value={currency}>
                    {currency}
                  </option>
                ))}
              </select>
            </label>
            <label className="step-form-choice">
              {t('onboarding.matchSetupPayPeriodLabel')}
              <select
                value={form.payPeriod}
                disabled={form.payNoPreference}
                onChange={(event) => patch({ payPeriod: event.target.value })}
              >
                {PAY_PERIODS.map((period) => (
                  <option key={period.value} value={period.value}>
                    {t(period.labelKey)}
                  </option>
                ))}
              </select>
            </label>
            <label className="step-form-choice">
              {t('onboarding.matchSetupPayGrossOrNetLabel')}
              <select
                value={form.payGrossOrNet}
                disabled={form.payNoPreference}
                onChange={(event) => patch({ payGrossOrNet: event.target.value })}
              >
                {PAY_GROSS_OR_NET.map((option) => (
                  <option key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="step-form-opt-out">
            <input
              type="checkbox"
              checked={form.payNoPreference}
              onChange={(event) => patch({ payNoPreference: event.target.checked })}
            />
            {t('onboarding.matchSetupPayNoPreference')}
          </label>
        </div>

        <div className="step-form-field">
          <label htmlFor="match-setup-exclusions" className="step-form-label">
            {t('onboarding.matchSetupExclusionsLabel')}
          </label>
          <textarea
            id="match-setup-exclusions"
            rows={3}
            value={form.exclusions}
            onChange={(event) => patch({ exclusions: event.target.value })}
          />
          <p className="step-form-hint">{t('onboarding.matchSetupExclusionsHint')}</p>
        </div>

        <fieldset className="step-form-field">
          <legend className="step-form-label">{t('onboarding.matchSetupEmployerLabel')}</legend>
          <p className="step-form-hint">{t('onboarding.matchSetupEmployerHint')}</p>
          {EMPLOYER_SCOPES.map((scope) => {
            const strength = form.employerStrengths[scope.value]
            return (
              <div key={scope.value} className="step-form-field">
                <label className="step-form-choice">
                  <input
                    type="checkbox"
                    checked={strength !== undefined}
                    onChange={(event) => {
                      const next = { ...form.employerStrengths }
                      // Ticking opens the strength question on `hard`, the
                      // literal meaning of the box the person just ticked
                      // ("I will not work for these"); untickking removes the
                      // refusal entirely rather than leaving a soft ghost.
                      if (event.target.checked) next[scope.value] = 'hard'
                      else delete next[scope.value]
                      patch({ employerStrengths: next })
                    }}
                  />
                  {t(scope.labelKey)}
                </label>
                {strength !== undefined && (
                  <fieldset className="step-form-field">
                    <legend className="step-form-label">
                      {t('onboarding.matchSetupEmployerStrengthLabel')}
                    </legend>
                    {EMPLOYER_STRENGTHS.map((option) => (
                      <label key={option.value} className="step-form-choice">
                        <input
                          type="radio"
                          name={`employer-strength-${scope.value}`}
                          value={option.value}
                          checked={strength === option.value}
                          onChange={() =>
                            patch({
                              employerStrengths: {
                                ...form.employerStrengths,
                                [scope.value]: option.value,
                              },
                            })
                          }
                        />
                        {t(option.labelKey)}
                      </label>
                    ))}
                  </fieldset>
                )}
              </div>
            )
          })}
        </fieldset>

        <fieldset className="step-form-field">
          <legend className="step-form-label">{t('onboarding.matchSetupLanguagesLabel')}</legend>
          <p className="step-form-hint">{t('onboarding.matchSetupLanguagesHint')}</p>
          <div className="step-form-levels">
            {LANGUAGES.map((language) => (
              <label key={language.value} className="step-form-level">
                {t(language.labelKey)}
                <select
                  value={form.levels[language.value]}
                  onChange={(event) =>
                    patch({ levels: { ...form.levels, [language.value]: event.target.value } })
                  }
                >
                  {LEVELS.map((level) => (
                    <option key={level.value} value={level.value} disabled={level.value === ''}>
                      {t(level.labelKey)}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </div>
        </fieldset>
      </div>

      {error && (
        <p className="form-error" role="alert">
          {t(error)}
        </p>
      )}

      <button
        type="button"
        className="btn btn-primary"
        aria-busy={saving}
        disabled={saving}
        onClick={() => void submit()}
      >
        {t('onboarding.matchSetupSubmit')}
      </button>
    </div>
  )
}
